<template>
  <base-modal
    class="modal-teacher"
    title="Edit Profile Information"
    @close="$emit('close')"
    data-test-id="edit-profile-modal"
  >
    <form class="modal-teacher__form" @submit.prevent="save" data-test-id="profile-info-form">
      <form-control label="INFORMATION" theme="dark" data-test-id="information-section">
        <div class="modal-teacher__columns">
          <div class="modal-teacher__column">
            <label for="name">Name</label>
          </div>
          <div class="modal-teacher__column">
            <text-field :invalid="hasErrors" v-model="model.name" theme="dark" data-test-id="name-input"></text-field>
          </div>
        </div>
        <div class="modal-teacher__columns">
          <div class="modal-teacher__column">
            <label for="email">E-mail</label>
          </div>
          <div class="modal-teacher__column">
            <text-field :invalid="hasErrors" v-model="model.email" theme="dark" data-test-id="email-input"></text-field>
          </div>
        </div>
        <div class="modal-teacher__columns">
          <div class="modal-teacher__column">
            <label for="experience">YEAR OF EXPERIENCES</label>
          </div>
          <div class="modal-teacher__column">
            <v-date-picker v-model="experienceDate">
              <text-field
                :invalid="hasErrors"
                :value="model.experience_date"
                readonly
                theme="dark"
                data-test-id="experience-input"
              ></text-field>
            </v-date-picker>
          </div>
        </div>
      </form-control>

      <form-control label="TEACHING LANGUAGES" theme="dark" data-test-id="teaching-languages-section">
        <div class="modal-teacher__columns modal-teacher__checkbox">
          <label v-for="language in model.teaching_languages" :key="language.code" data-test-id="language-checkbox">
            <input
              type="checkbox"
              name="language-taught"
              v-model="language.active"
              :id="language.code"
              :value="language.active"
            />
            {{ language.name }}
          </label>
        </div>
      </form-control>

      <form-control
        v-if="restrictedSectionVisible"
        label="INTERNAL ADMIN USE"
        theme="dark"
        data-test-id="admin-use-section"
      >
        <div class="modal-teacher__columns">
          <div class="modal-teacher__column">
            <label for="active">ACTIVE</label>
          </div>
          <div class="modal-teacher__column">
            <input
              type="checkbox"
              id="active"
              v-model="model.active"
              :disabled="isSaving"
              data-test-id="active-checkbox"
            />
          </div>
        </div>
        <div class="modal-teacher__columns">
          <div class="modal-teacher__column">
            <label for="accent">TEACHER ACCENT</label>
          </div>
          <div class="modal-teacher__column">
            <text-field
              :invalid="hasErrors"
              v-model="model.accent"
              theme="dark"
              data-test-id="accent-input"
            ></text-field>
          </div>
        </div>
        <div class="modal-teacher__columns">
          <div class="modal-teacher__column">
            <label for="hourly_cost">HOURLY COST IN DOLLARS</label>
          </div>
          <div class="modal-teacher__column">
            <text-field
              :invalid="hasErrors"
              v-model="model.hourly_cost"
              theme="dark"
              data-test-id="hourly_cost-input"
            ></text-field>
          </div>
        </div>
        <div class="modal-teacher__columns">
          <div class="modal-teacher__column">
            <label for="location">LOCATION INFO</label>
          </div>
          <div class="modal-teacher__column">
            <text-field
              :invalid="hasErrors"
              v-model="model.location"
              theme="dark"
              data-test-id="location-input"
            ></text-field>
          </div>
        </div>
      </form-control>

      <div class="modal-teacher__submit">
        <p v-if="errors.length" data-test-id="error-message">{{ errors }}</p>
        <voxy-button
          view="primary"
          type="submit"
          size="s"
          :disabled="$v.model.$invalid || isSaving"
          data-test-id="submit-button"
        >
          {{ isSaving ? 'Saving...' : 'Save' }}
        </voxy-button>
      </div>
    </form>
  </base-modal>
</template>

<script>
import { mapActions } from 'vuex';
import { VoxyButton } from '@voxy-team/vue-voxy';
import { email, required } from 'vuelidate/lib/validators';

import FormErrorMapper from '@/mixins/FormErrorMapper';

import BaseModal from '@/components/BaseModal';
import FormControl from '@/components/form/FormControl';
import TextField from '@/components/form/TextField';

export default {
  name: 'ModalTeacherPrimaryInfo',

  components: {
    BaseModal,
    FormControl,
    VoxyButton,
    TextField,
  },

  mixins: [FormErrorMapper],

  props: {
    teacherProfile: {
      default: () => ({}),
      required: true,
      type: Object,
    },
    restrictedSectionVisible: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    const today = new Date();
    return {
      errors: {},
      hasErrors: false,
      isSaving: false,
      languages: this.teacherProfile.teaching_languages.map(language => {
        return language.name;
      }),
      model: {
        name: this.teacherProfile.name,
        email: this.teacherProfile.email,
        experience_date: this.teacherProfile.experience_date,
        teaching_languages: this.teacherProfile.teaching_languages,
        accent: this.teacherProfile.accent,
        location: this.teacherProfile.location,
        hourly_cost: this.teacherProfile.hourly_cost_in_dollars,
        active: this.teacherProfile.active,
      },
      experienceDate: this.teacherProfile.experience_date ? new Date(this.teacherProfile.experience_date) : today,
    };
  },

  watch: {
    experienceDate(value) {
      this.model.experience_date = new Date(value).toJSON();
    },
  },

  validations: {
    model: {
      name: {
        required,
      },
      email: {
        required,
        email,
      },
      experience_date: {
        required,
      },
      teaching_languages: {
        required,
      },
    },
  },

  methods: {
    ...mapActions('manageTeachers', {
      updateTeacherProfile: 'UPDATE_TEACHER_PROFILE',
    }),

    formatDataPatch() {
      const formatLanguages = this.model.teaching_languages
        .filter(language => {
          return language.active;
        })
        .map(language => {
          return language.code;
        });

      return {
        ...this.model,
        teaching_languages: formatLanguages,
      };
    },

    async save() {
      this.errors = {};

      try {
        await this.updateTeacherProfile({
          teacherHashEmail: this.$route.params.teacherHashEmail,
          value: this.formatDataPatch(),
        });
        this.$emit('close');
      } catch (errors) {
        this.errors = JSON.stringify(errors);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/components/teacher-management-sections';
</style>
